import { useEffect, useMemo } from 'react';
import { DEFAULT_MAIN_COLUMNS, MAIN_COLUMN_KEYS } from '@/entities/conversations';
import type { SearchDialogsPage } from '@/entities/search';
import type { ExpertReportSettings } from '@/entities/settings';
import { ATTRIBUTE_COLUMN_PREFIX, EXPERT_REPORT_STANDARD_COLUMNS } from './constants';
import type { DialogMainColumn } from './types';
import type { ExpertReportBlocksMap } from './useDialogTableColumns';

/** Параметры сборки параметров запроса из видимости колонок */
type Params = {
  /** Все атрибуты диалога (из `useDialogColumnsData`) */
  allAttributes: { path: string }[];
  /** Все экспертные заключения (из `useDialogColumnsData`) */
  allReports: { id: number }[];
  /** Блоки заключений по id заключения */
  blocksByReportId: ExpertReportBlocksMap;
  /** Видимость колонок DataGrid */
  columnVisibility: Record<string, boolean>;
  /** Стейт колонок готов — до этого параметры отчётов не считаем */
  isReady: boolean;
  /** Сохранённый в профиле состав основных колонок */
  mainColumns: string[] | undefined;
  /** Записывает состав основных колонок в настройки профиля */
  onMainColumnsChange: (columns: DialogMainColumn[]) => void;
};

/** Параметры запроса, вычисленные из видимости колонок */
type Result = {
  /** Пути видимых атрибутов — отправляются на бэк */
  visibleAttributePaths: string[];
  /** Видимые основные колонки в каноническом порядке */
  visibleMainColumns: DialogMainColumn[];
  /** Параметры видимых экспертных заключений (колонки и блоки) */
  expertReportSearchParams: ExpertReportSettings[];
  /** Собранный `dialogsPage` для запроса списка */
  searchDialogsPage: SearchDialogsPage;
};

/**
 * Собирает параметры запроса списка диалогов из текущей видимости колонок: пути видимых атрибутов,
 * видимые основные колонки в каноническом порядке и параметры видимых экспертных заключений.
 * Синхронизирует состав основных колонок с настройками профиля.
 */
export const useDialogSearchParams = ({
  allAttributes,
  allReports,
  blocksByReportId,
  columnVisibility,
  isReady,
  mainColumns,
  onMainColumnsChange,
}: Params): Result => {
  const visibleAttributePaths = useMemo(
    () =>
      allAttributes
        .filter((attr) => columnVisibility[`${ATTRIBUTE_COLUMN_PREFIX}${attr.path}`] !== false)
        .map((attr) => attr.path),
    [allAttributes, columnVisibility],
  );

  const expertReportSearchParams = useMemo<ExpertReportSettings[]>(() => {
    if (!isReady) return [];
    return allReports
      .map((report) => {
        const columns = EXPERT_REPORT_STANDARD_COLUMNS.filter(
          (col) => columnVisibility[`expertReport_${report.id}_${col}`] !== false,
        );
        const blockIds: number[] = [];
        blocksByReportId.get(report.id)?.forEach((_, blockId) => {
          if (columnVisibility[`expertReport_${report.id}_block_${blockId}`] !== false) {
            blockIds.push(blockId);
          }
        });
        return { id: report.id, columns, blockIds };
      })
      .filter((s) => s.columns.length > 0 || s.blockIds.length > 0);
  }, [allReports, blocksByReportId, columnVisibility, isReady]);

  // Видимые основные колонки в каноническом порядке из MAIN_COLUMN_KEYS — отправляются на бэк
  // в POST /search/query (dialogsPage.mainColumns) и синхронизируются в PUT /profile/ui-settings.
  const visibleMainColumns = useMemo(
    () => MAIN_COLUMN_KEYS.filter((key) => columnVisibility[key] !== false) as DialogMainColumn[],
    [columnVisibility],
  );

  // Синхронизация видимости основных колонок с настройками профиля.
  // Сравниваем по значениям, чтобы не дёргать setDialogsSettings при идентичных массивах.
  useEffect(() => {
    if (!isReady) return;
    const current = mainColumns ?? DEFAULT_MAIN_COLUMNS;
    const same =
      current.length === visibleMainColumns.length &&
      current.every((value, index) => value === visibleMainColumns[index]);
    if (same) return;
    onMainColumnsChange(visibleMainColumns);
  }, [visibleMainColumns, mainColumns, isReady, onMainColumnsChange]);

  const searchDialogsPage = useMemo<SearchDialogsPage>(
    () => ({
      attributes: visibleAttributePaths,
      mainColumns: visibleMainColumns,
      expertReport: expertReportSearchParams,
    }),
    [visibleAttributePaths, visibleMainColumns, expertReportSearchParams],
  );

  return { visibleAttributePaths, visibleMainColumns, expertReportSearchParams, searchDialogsPage };
};
